import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, FileCode2, Coffee, Database, Braces, Cpu, BrainCircuit, Trash2, Pencil, FolderCode, ArrowLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import TopNav from "@/components/TopNav";
import { getWs, setWs, saveWsToDb, starterFiles, categoryOf } from "@/lib/workspace";

const LANGS = [
  { key: "html", category: "frontend", label: "HTML / CSS / JS", folder: "HTML", def: "HTML Project", sub: "Live web preview", icon: Globe, tint: "bg-[#3b2a1e]", color: "text-[#e08a3c]" },
  { key: "python", category: "frontend", label: "Python", folder: "Python", def: "Python Project", sub: "Versatile & readable", icon: FileCode2, tint: "bg-[#1e2f4a]", color: "text-[#5aa9e6]" },
  { key: "java", category: "frontend", label: "Java", folder: "Java", def: "Java Project", sub: "Enterprise & robust", icon: Coffee, tint: "bg-[#3b2a1e]", color: "text-[#e08a3c]" },
  { key: "sql", category: "backend", label: "SQL / Database", folder: "SQL", def: "SQL Project", sub: "Query & analyse data", icon: Database, tint: "bg-[#173f4a]", color: "text-teal-accent" },
  { key: "ml", category: "frontend", label: "Machine Learning / AI", folder: "Machine Learning", def: "ML Project", sub: "NumPy & data science", icon: BrainCircuit, tint: "bg-[#3b2318]", color: "text-[#d9703c]" },
  { key: "cpp", category: "frontend", label: "C++", folder: "C++", def: "C++ Project", sub: "High performance", icon: Cpu, tint: "bg-[#1e2f4a]", color: "text-[#5aa9e6]" },
  { key: "javascript", category: "frontend", label: "JavaScript (Node)", folder: "JavaScript", def: "JavaScript Project", sub: "Full-stack scripting", icon: Braces, tint: "bg-[#33361e]", color: "text-[#c8d44a]" },
];
const FOLDER_LABEL = { html: "HTML", python: "Python", java: "Java", javascript: "JavaScript", cpp: "C++", sql: "SQL", ml: "Machine Learning" };
const folderLabel = (lang) => FOLDER_LABEL[lang] || lang.toUpperCase();

export default function CodingHub() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [rename, setRename] = useState(null);
  const [createModal, setCreateModal] = useState(null); // {category, language, folder, name}
  const [openFolder, setOpenFolder] = useState(null); // language key

  const load = async () => {
    try { const { data } = await api.get("/projects"); setProjects(data); } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = (l) => {
    const ws = getWs(l.key);
    // Returning to a language that already has an active project (saved or unsaved)
    // must reopen that exact project — never a brand-new empty one.
    if (ws && (ws.projectId || (ws.files && ws.files.length))) {
      nav(ws.projectId ? `/editor/${ws.projectId}` : `/editor/new/${l.key}`);
      return;
    }
    setCreateModal({ category: l.category, language: l.key, folder: l.folder, name: l.def });
  };
  const doCreate = () => {
    const name = createModal.name.trim();
    if (!name) return toast.error("Enter a project name");
    const lang = createModal.language;
    const sf = starterFiles(lang);
    // create a TEMPORARY (unsaved) project — persisted only when the user clicks Save in the editor
    setWs(lang, { projectId: null, name, language: lang, category: createModal.category,
      files: sf, active: sf[0].name, openTabs: [sf[0].name], cursor: { line: 1, col: 1 }, scroll: 0, dirty: true });
    setCreateModal(null);
    nav(`/editor/new/${lang}`);
  };

  // Auto-save rule: if a different unsaved/dirty project of the SAME language is open, save it first.
  const openSaved = async (p) => {
    try {
      const ws = getWs(p.language);
      if (ws && ws.dirty && ws.projectId !== p.id) {
        await saveWsToDb(ws);
        toast.success(`'${ws.name}' auto-saved`);
      }
    } catch (e) { /* non-blocking */ }
    nav(`/editor/${p.id}`);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted"); load();
  };
  const doRename = async () => {
    await api.put(`/projects/${rename.id}`, { name: rename.name });
    setRename(null); toast.success("Renamed"); load();
  };

  // group all projects by language folder
  const byLang = {};
  projects.forEach((p) => { (byLang[p.language] = byLang[p.language] || []).push(p); });
  const folderProjects = openFolder ? (byLang[openFolder] || []) : [];

  return (
    <div className="min-h-screen">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-24">
        <span className="pill text-[11px] tracking-[0.2em] px-4 py-1.5 glass text-teal-accent inline-block">CODE STUDIO</span>
        <h1 className="serif text-4xl sm:text-5xl font-extrabold mt-5">Start a new project</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">Spin up a real multi-file workspace with a VS Code-style editor, terminal, AI assistant and live preview.</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
          {LANGS.map((l) => (
            <button key={l.key} data-testid={`create-${l.key}`} onClick={() => openCreate(l)}
                    className="glass glass-hover rounded-2xl p-6 flex items-center gap-4 text-left">
              <div className={`w-12 h-12 rounded-xl ${l.tint} flex items-center justify-center shrink-0`}>
                <l.icon size={22} className={l.color} />
              </div>
              <div><div className="font-semibold">{l.label}</div><div className="text-xs text-muted-foreground">{l.sub}</div></div>
            </button>
          ))}
        </div>

        <h2 className="serif text-2xl font-bold mt-14 mb-5 flex items-center gap-3">
          <FolderCode size={22} className="text-teal-accent" /> Your projects
        </h2>

        {!openFolder ? (
          Object.keys(byLang).length === 0 ? (
            <p className="text-muted-foreground text-sm">No projects yet. Create one above to get started.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {Object.entries(byLang).map(([lang, list]) => (
                <button key={lang} data-testid={`folder-${lang}`} onClick={() => setOpenFolder(lang)}
                        className="glass glass-hover rounded-2xl p-6 flex items-center justify-between text-left">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#00D4D4]/12 flex items-center justify-center"><FolderCode size={22} className="text-teal-accent" /></div>
                    <div><div className="font-semibold">{folderLabel(lang)}</div><div className="text-xs text-muted-foreground">{list.length} project{list.length !== 1 ? "s" : ""}</div></div>
                  </div>
                  <ChevronRight size={18} className="text-muted-foreground" />
                </button>
              ))}
            </div>
          )
        ) : (
          <div>
            <button data-testid="folder-back" onClick={() => setOpenFolder(null)} className="flex items-center gap-2 text-sm text-teal-accent hover:text-white mb-5"><ArrowLeft size={16} /> Back to folders</button>
            <h3 className="serif text-xl font-bold mb-4">{folderLabel(openFolder)} projects</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {folderProjects.map((p) => (
                <div key={p.id} data-testid={`project-${p.id}`} className="glass glass-hover rounded-2xl p-5 group">
                  <button onClick={() => openSaved(p)} className="text-left w-full">
                    <div className="flex items-center gap-2 text-xs text-teal-accent uppercase tracking-wider mb-2">{folderLabel(p.language)} · {p.files?.length || 0} files</div>
                    <div className="font-semibold text-lg truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">Updated {new Date(p.updated_at).toLocaleDateString()}</div>
                  </button>
                  <div className="flex gap-2 mt-4 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button data-testid={`edit-${p.id}`} onClick={() => openSaved(p)} className="p-2 rounded-lg hover:bg-white/5 text-muted-foreground" title="Edit"><Pencil size={15} /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="p-2 rounded-lg hover:bg-white/5 text-red-400" title="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setCreateModal(null)}>
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="create-project-modal">
            <h3 className="serif text-xl mb-1">Create a new project</h3>
            <p className="text-xs text-muted-foreground mb-4">{createModal.folder} · you can rename it</p>
            <input data-testid="create-project-name" value={createModal.name} onChange={(e) => setCreateModal({ ...createModal, name: e.target.value })}
                   onKeyDown={(e) => e.key === "Enter" && doCreate()} autoFocus className="kiq-input w-full px-4 py-3 text-sm mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setCreateModal(null)} className="flex-1 py-2.5 rounded-full glass text-sm">Cancel</button>
              <button data-testid="create-project-save" onClick={doCreate} className="flex-1 kiq-btn py-2.5 text-sm">Save</button>
            </div>
          </div>
        </div>
      )}

      {rename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setRename(null)}>
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="serif text-xl mb-4">Rename project</h3>
            <input data-testid="rename-input" value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })}
                   className="kiq-input w-full px-4 py-3 text-sm mb-4" autoFocus />
            <div className="flex gap-3">
              <button onClick={() => setRename(null)} className="flex-1 py-2.5 rounded-full glass text-sm">Cancel</button>
              <button data-testid="rename-save" onClick={doRename} className="flex-1 kiq-btn py-2.5 text-sm">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
